import React, { useMemo } from 'react';
import { suggestedActivities } from '../data/suggestedActivities.js';
import { cpdCategories } from '../data/cpdData.js';
import { ArrowRight, Lightbulb } from 'lucide-react';

const SuggestedActivities = ({ categoryHours }) => {

    const suggestions = useMemo(() => {
        // Find the user's top category (most hours)
        const topCategory = Object.keys(categoryHours).reduce((a, b) => categoryHours[a] > categoryHours[b] ? a : b, null);

        // Find all categories the user has logged 0 hours in
        const zeroHourCategories = cpdCategories
            .map(c => c.name)
            .filter(name => !Object.keys(categoryHours).includes(name) || categoryHours[name] === 0);

        let finalSuggestions = [];

        // 1. Find a suggestion in a zero-hour category that matches the user's top category interests
        if (topCategory && zeroHourCategories.length > 0) {
            const topKeywords = suggestedActivities
                .filter(act => act.category === topCategory)
                .flatMap(act => act.keywords);
            
            const bestMatch = suggestedActivities.find(suggestion => 
                zeroHourCategories.includes(suggestion.category) &&
                suggestion.keywords.some(kw => topKeywords.includes(kw))
            );
            if (bestMatch) {
                finalSuggestions.push({ ...bestMatch, reason: `Balance your CPD by exploring '${bestMatch.category}'` });
            }
        }

        // 2. Find a suggestion for the category with the lowest hours (but not zero)
        const nonZeroCategories = Object.entries(categoryHours).sort((a, b) => a[1] - b[1]);
        if (nonZeroCategories.length > 0) {
            const lowestCategoryName = nonZeroCategories[0][0];
            const lowestCatSuggestion = suggestedActivities.find(suggestion => 
                suggestion.category === lowestCategoryName && !finalSuggestions.some(s => s.id === suggestion.id)
            );
            if (lowestCatSuggestion) {
                finalSuggestions.push({ ...lowestCatSuggestion, reason: `Deepen your knowledge in '${lowestCategoryName}'` });
            }
        }
        
        // 3. Add a general "high impact" suggestion as a fallback
        if (finalSuggestions.length < 2) {
            const highImpactSuggestion = suggestedActivities.find(suggestion => 
                suggestion.keywords.includes("sabr") && suggestion.hours > 10 && !finalSuggestions.some(s => s.id === suggestion.id)
            );
            if (highImpactSuggestion) {
                finalSuggestions.push({ ...highImpactSuggestion, reason: "Consider a high-impact course" });
            }
        }

        return finalSuggestions.slice(0, 2); // Return a maximum of 2 suggestions

    }, [categoryHours]);

    if (suggestions.length === 0) {
        return null; // Don't render anything if no suggestions are found
    }

    const Card = ({ suggestion }) => (
        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg flex flex-col justify-between border border-gray-200 dark:border-gray-700">
            <div>
                <p className="text-xs font-semibold text-blue-500 dark:text-blue-400 mb-1">{suggestion.reason}</p>
                <h4 className="font-bold text-gray-800 dark:text-gray-200">{suggestion.title}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{suggestion.description}</p>
            </div>
            <a 
                href={suggestion.link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-500"
            >
                Learn More <ArrowRight size={16} className="ml-1" />
            </a>
        </div>
    );

    return (
        <div className="mt-8">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                <Lightbulb size={22} className="mr-3 text-yellow-400" />
                Suggested Next Activities
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {suggestions.map(sugg => <Card key={sugg.id} suggestion={sugg} />)}
            </div>
        </div>
    );
};

export default SuggestedActivities;